import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { IconRefresh, IconClock } from '@tabler/icons-react';
import type { Vendor, VendorImportBatch, VendorImportRow } from '@techgitter/contracts';
import { getVendors } from '../../services/vendorService';
import { fetchTrialRows, fetchImportBatches } from '../../services/reconciliationApi';
import { textMatches } from './reconciliationFilter';
import { ReconciliationFilterFields } from './ReconciliationFilterFields';
import {
  SecondaryToolbar,
  ToolbarSelect,
  ToolbarChip,
  ToolbarSpacer,
  ToolbarButton,
} from '../toolbar';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../table';
import { Chip } from '../chip';
import { CARD_SX } from '../../styles/designSystem';

/**
 * The Trials area: active-but-free trials (25-seat Microsoft NCE trials —
 * `billingFrequency == 'none'`, Monthly commitment term) for a supplier's latest
 * import. The engine parks these — they never appear as breaks, variances, or in
 * the unmatched worklist. A trial with vendor auto-renew ON (`trialAutoRenews`)
 * will convert to a paid commitment at its renewal date, so it is flagged with a
 * warning for an operator to action (turn off auto-renew, or convert it) before
 * it starts billing. Each trial's expiry (renewal) date is shown, and the
 * "Hide long-expired" filter (on by default) narrows the list to the actionable
 * ones — trials not yet expired plus any that expired within the last
 * {@link RECENT_EXPIRY_WINDOW_DAYS} days. Read-only. See
 * docs/architecture/trial-subscription-handling.md §3.
 */
// A trial is "recent or upcoming" when its expiry is no more than this many days
// in the past — long-expired trials are hidden by default (toggle to show all).
const RECENT_EXPIRY_WINDOW_DAYS = 3;

interface Props {
  customerFilter?: string;
  productFilter?: string;
  onCustomerFilterChange?: (v: string) => void;
  onProductFilterChange?: (v: string) => void;
}

export const TrialsPanel: React.FC<Props> = ({
  customerFilter = '',
  productFilter = '',
  onCustomerFilterChange,
  onProductFilterChange,
}) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorId, setVendorId] = useState<string>('');
  const [batch, setBatch] = useState<VendorImportBatch | null>(null);
  const [rows, setRows] = useState<VendorImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // On by default: hide trials that expired more than RECENT_EXPIRY_WINDOW_DAYS
  // ago, keeping the view on the actionable (upcoming + just-expired) ones.
  const [recentOnly, setRecentOnly] = useState(true);

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.vendorId, label: v.name })),
    [vendors],
  );

  useEffect(() => {
    const controller = new AbortController();
    getVendors(controller.signal)
      .then((all) => {
        if (controller.signal.aborted) return;
        const suppliers = all.filter((v) => v.isSubscriptionSupplier);
        setVendors(suppliers);
        if (suppliers.length && !vendorId) setVendorId(suppliers[0].vendorId);
      })
      .catch((e) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError('Could not load subscription suppliers.');
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!vendorId) return;
      setLoading(true);
      setError(null);
      try {
        const [batches, trials] = await Promise.all([
          fetchImportBatches({ vendorId, top: 1 }, signal),
          fetchTrialRows({ vendorId }, signal),
        ]);
        if (signal?.aborted) return;
        setBatch(batches[0] ?? null);
        setRows(trials);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load trial rows');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [vendorId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const sorted = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            textMatches(customerFilter, r.customerIdentifier) &&
            textMatches(productFilter, r.sku),
        )
        .sort((a, b) => (a.customerIdentifier ?? '').localeCompare(b.customerIdentifier ?? '')),
    [rows, customerFilter, productFilter],
  );

  // Apply the "recently expired + upcoming" filter. A row with no (or an
  // unparseable) renewal date can't be judged stale, so it's always kept.
  const visible = useMemo(() => {
    if (!recentOnly) return sorted;
    const cutoff = Date.now() - RECENT_EXPIRY_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return sorted.filter((r) => {
      if (!r.renewalDate) return true;
      const t = new Date(r.renewalDate).getTime();
      return Number.isNaN(t) || t >= cutoff;
    });
  }, [sorted, recentOnly]);
  const hiddenCount = sorted.length - visible.length;

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Supplier"
          value={vendorId}
          onChange={setVendorId}
          options={vendorOptions}
        />
        {batch && (
          <Typography sx={{ fontSize: 12, color: '#888' }}>
            Latest import: {fmtDate(batch.importedAt)} · {batch.rowCount ?? '—'} rows
          </Typography>
        )}
        <ReconciliationFilterFields
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={onCustomerFilterChange ?? (() => {})}
          onProductFilterChange={onProductFilterChange ?? (() => {})}
        />
        <ToolbarChip
          label="Hide long-expired"
          active={recentOnly}
          onClick={() => setRecentOnly((v) => !v)}
          icon={IconClock}
        />
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>
          Active-but-free trials (25-seat Microsoft NCE trials). Parked — not counted as
          breaks or variances. An auto-renewing trial will convert to billing at its
          renewal date and should be actioned before it does. Showing trials not yet
          expired plus any expired within the last {RECENT_EXPIRY_WINDOW_DAYS} days —
          turn off “Hide long-expired” for the full list.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : visible.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: '#888', px: 0.5, py: 2 }}>
            {sorted.length === 0
              ? 'No trials in this supplier’s latest import. 🎉'
              : `All ${sorted.length} trial${sorted.length === 1 ? '' : 's'} in this import expired more than ${RECENT_EXPIRY_WINDOW_DAYS} days ago — turn off “Hide long-expired” to show them.`}
          </Typography>
        ) : (
          <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
            <Box sx={{ p: '12px 16px', borderBottom: '0.5px solid #e2eaf3', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Trials</Typography>
              <Chip>{visible.length}</Chip>
              {recentOnly && hiddenCount > 0 && (
                <Typography sx={{ fontSize: 12, color: '#888' }}>
                  · {hiddenCount} long-expired hidden
                </Typography>
              )}
            </Box>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Customer</DataTableHeadCell>
                  <DataTableHeadCell>SKU</DataTableHeadCell>
                  <DataTableHeadCell>Term</DataTableHeadCell>
                  <DataTableHeadCell align="right">Seats</DataTableHeadCell>
                  <DataTableHeadCell>Tenant</DataTableHeadCell>
                  <DataTableHeadCell>Expires</DataTableHeadCell>
                  <DataTableHeadCell>Auto-renew</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <DataTableRow key={r.id}>
                    <DataTableCell>{r.customerIdentifier ?? '—'}</DataTableCell>
                    <DataTableCell>{r.sku ?? '—'}</DataTableCell>
                    <DataTableCell>{r.billingTerm ? <Chip>{r.billingTerm}</Chip> : '—'}</DataTableCell>
                    <DataTableCell align="right">{r.seats ?? '—'}</DataTableCell>
                    <DataTableCell muted>{r.tenantDomain ?? '—'}</DataTableCell>
                    <DataTableCell>{fmtDate(r.renewalDate)}</DataTableCell>
                    <DataTableCell>
                      {r.trialAutoRenews ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip tone="warning" title="This trial has auto-renew ON — it will convert to a paid commitment at its renewal date.">
                              will convert to billing
                            </Chip>
                            {r.conversion?.imminent && (
                              <Chip tone="warning" title="Converts within the imminence window — act soon">
                                imminent
                              </Chip>
                            )}
                          </Box>
                          {r.conversion && (
                            <Typography sx={{ fontSize: 11, color: '#888' }}>
                              → {r.conversion.scheduledPlan ?? 'paid plan'}
                              {typeof r.conversion.scheduledSeats === 'number'
                                ? ` · ${r.conversion.scheduledSeats} seats`
                                : ''}
                              {r.conversion.scheduledBillingTerm
                                ? ` · ${r.conversion.scheduledBillingTerm}`
                                : ''}
                              {r.conversion.effectiveDate
                                ? ` · ${fmtDate(r.conversion.effectiveDate)}`
                                : ''}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography component="span" sx={{ fontSize: 12, color: '#888' }}>
                          no auto-renew
                        </Typography>
                      )}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTable>
          </Box>
        )}
      </Box>
    </>
  );
};

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}
